import axios from 'axios';
import { axiosInstance } from 'common/network';

const API_ROOT = process.env.REACT_APP_API_ROOT;

const applicationsUrl = () => `${API_ROOT}v3/licensed_applications`;
const applicationUrl = (application) =>
  `${applicationsUrl()}/${encodeURIComponent(application)}`;
const packageUrl = (application, uuid) =>
  `${applicationUrl(application)}/packages/${encodeURIComponent(uuid)}`;
const accessUrl = (application, uuid) => `${packageUrl(application, uuid)}/access`;

// __links come back root-relative ("/v3/licensed_applications/..."). Resolve them
// against the API root, and refuse any that leave its origin: every call below
// sends the bearer token.
const resolve = (link) => {
  const url = new URL(link, API_ROOT);
  if (url.origin !== new URL(API_ROOT).origin) {
    throw new Error(`Refusing to send credentials to ${url.origin}`);
  }
  return url.toString();
};

// Package objects are snake_case; responses assembled from anonymous objects
// (register, PATCH, verify, upload_urls) are camelCase. Both shapes appear in one
// flow, so nothing here normalises them — call sites use the shape they get.

export const packageStatus = (pkg) => {
  if (!pkg || !pkg.is_published) return 'Draft';
  return pkg.is_visible ? 'Published' : 'Hidden';
};

// Shared so a status reads the same in the list and on the detail page.
export const packageStatusColor = (status) => {
  if (status === 'Published') return 'success';
  if (status === 'Hidden') return 'warning';
  return 'default';
};

export const formatSize = (bytes) => {
  const value = Number(bytes);
  if (!value || value < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(
    Math.floor(Math.log(value) / Math.log(1024)),
    units.length - 1
  );
  const scaled = value / Math.pow(1024, exponent);
  return `${scaled.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

// Every failure carries { code, message }. Views switch on code, never on status.
export const apiError = (error) => {
  const data = error && error.response ? error.response.data : null;
  return {
    status: error && error.response ? error.response.status : null,
    code: data && data.code ? data.code : null,
    message:
      (data && data.message) ||
      (error && error.message) ||
      'The request failed.',
  };
};

// compatibility and manifest are raw JSON editors, validated before submit so a
// parse error never becomes a round trip. Empty is valid: both are optional.
// Returns '' when the value is acceptable.
export const jsonFieldError = (value, mustBeArray) => {
  if (!value || !value.trim()) return '';
  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    return e.message;
  }
  if (mustBeArray && !Array.isArray(parsed)) {
    return 'Must be a JSON array, e.g. ["version expression","semver expression"]';
  }
  return '';
};

// One copy of the wording, because two of these must never collapse into each
// other: already_published means "you may not re-upload over this",
// duplicate_name_type_version means "pick another version".
export const errorMessage = (failure) => {
  switch (failure.code) {
    case 'already_published':
      return 'This version is already published — unpublish it or bump the version.';
    case 'duplicate_name_type_version':
      return 'A package with that name, type and version already exists.';
    case 'duplicate_location':
      return 'Another package already occupies that storage path.';
    case 'invalid_compatibility':
      return 'Compatibility is not valid JSON.';
    case 'invalid_manifest':
      return 'Manifest is not valid JSON.';
    case 'package_not_found':
      return 'That package no longer exists.';
    case 'account_not_found':
      return 'That account does not exist.';
    case 'duplicate_grant':
      return 'That account already has access to this package.';
    case 'grant_not_found':
      return 'That grant no longer exists — someone else may have removed it already.';
    default:
      return failure.message;
  }
};

// The access endpoints land after this UI. A 404 from a route that does not exist
// yet carries no body, so no code; a 404 that names a code is a real answer from
// a deployed endpoint and must not be excused as "not deployed".
export const isEndpointMissing = (failure) =>
  Boolean(failure) && failure.status === 404 && !failure.code;

const applications = () =>
  axiosInstance
    .get(applicationsUrl())
    .then((response) => response.data.licensed_applications || []);

const permissions = () =>
  axiosInstance.get(`${applicationsUrl()}/permissions`).then((r) => r.data);

// The admin list. Not /packages, which filters out exactly the drafts and hidden
// rows this UI exists to show.
const listManaged = (application) =>
  axiosInstance
    .get(`${applicationUrl(application)}/managed_packages`)
    .then((r) => r.data);

const get = (application, uuid) =>
  axiosInstance.get(packageUrl(application, uuid)).then((r) => r.data);

// register and patch take FormData: the actions are [FromForm], and setting
// Content-Type by hand would drop the multipart boundary.
const register = (application, formData) =>
  axiosInstance
    .post(`${applicationUrl(application)}/packages`, formData)
    .then((r) => r.data);

const patch = (application, uuid, formData) =>
  axiosInstance.patch(packageUrl(application, uuid), formData).then((r) => r.data);

const publish = (link) => axiosInstance.post(resolve(link)).then((r) => r.data);

const unpublish = (link) => axiosInstance.post(resolve(link)).then((r) => r.data);

const verify = (link) => axiosInstance.get(resolve(link)).then((r) => r.data);

const uploadUrls = (link) => axiosInstance.post(resolve(link)).then((r) => r.data);

const remove = (application, uuid) =>
  axiosInstance.delete(packageUrl(application, uuid)).then((r) => r.data);

// The grant list for one package, plus a `caller` object describing the
// requesting account's own position on it. snake_case, like the package list.
const accessList = (application, uuid) =>
  axiosInstance.get(accessUrl(application, uuid)).then((r) => r.data);

// [FromForm], like register and patch: FormData, and no hand-set Content-Type.
const grantAccess = (application, uuid, accountUuid) => {
  const form = new FormData();
  form.append('accountUuid', accountUuid);
  return axiosInstance.post(accessUrl(application, uuid), form).then((r) => r.data);
};

// Keyed by account UUID rather than grant UUID, so a row already on screen can
// be revoked without looking its grant up first.
const revokeAccess = (application, uuid, accountUuid) =>
  axiosInstance
    .delete(`${accessUrl(application, uuid)}/${encodeURIComponent(accountUuid)}`)
    .then((r) => r.data);

// Application-scoped, which is what makes is_licensed answerable per row.
const candidateAccounts = (application, params) =>
  axiosInstance
    .get(`${applicationUrl(application)}/accounts`, { params })
    .then((r) => r.data);

// download/thumb/icon 302 to a presigned URL by default. Didimo-Follow-Uri: false
// returns { uri, ttl } instead, so the redirect is never followed with our
// Authorization header still attached. The uri lives 5 minutes — hand it to an
// <img> or a link, never to state that outlives it.
const assetUri = (link) =>
  axiosInstance
    .get(resolve(link), { headers: { 'Didimo-Follow-Uri': 'false' } })
    .then((r) => ({ uri: r.data.uri, ttl: r.data.ttl }));

// The one call in this file that must use bare axios: a presigned URL carries its
// own credentials in the query string, so adding our Authorization header both
// hands the bearer token to S3 and makes S3 reject the request.
//
// contentType must match what the URL was signed for (the register/upload_urls
// contentType field for the artifact, image/png for thumbnail and icon), or S3
// answers SignatureDoesNotMatch.
const putToS3 = (url, file, options) => {
  const { contentType, onProgress } = options || {};
  return axios.put(url, file, {
    headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
    onUploadProgress: (event) => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded * 100) / event.total));
      }
    },
  });
};

const packages = {
  applications,
  permissions,
  listManaged,
  get,
  register,
  patch,
  publish,
  unpublish,
  verify,
  uploadUrls,
  remove,
  accessList,
  grantAccess,
  revokeAccess,
  candidateAccounts,
  assetUri,
  putToS3,
};

export default packages;
